import React, { useState, useEffect, useRef } from "react";
import { RefreshCw, Download, ZoomIn, ZoomOut } from "lucide-react";
import "./Playground.css";

const DocumentViewer = ({ documentUrl, documentType, documentName, onRegenerate, isCollapsed, onToggleCollapse }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [zoom, setZoom] = useState(100);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const iframeRef = useRef(null);
  const zoomRef = useRef(100); // Store zoom in ref to persist across collapse/expand
  const scrollPositionRef = useRef({ scrollLeft: 0, scrollTop: 0 });
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (documentType === "pdf" && documentUrl) {
      // For PDFs, we'll use an iframe or embed
      setLoading(true);
      // Simulate page count - in real implementation, use PDF.js to get actual page count
      setTotalPages(2);
      setLoading(false);
    } else if (documentType === "image") {
      setTotalPages(1);
    } else {
      setTotalPages(1);
    }
  }, [documentUrl, documentType]);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleZoomIn = () => {
    setZoom((prev) => {
      const newZoom = Math.min(prev + 25, 200);
      zoomRef.current = newZoom;
      return newZoom;
    });
  };

  const handleZoomOut = () => {
    setZoom((prev) => {
      const newZoom = Math.max(prev - 25, 50);
      zoomRef.current = newZoom;
      return newZoom;
    });
  };

  // Sync zoom ref with state
  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  // Maintain zoom and position when expanding from collapsed state
  useEffect(() => {
    if (wrapperRef.current) {
      const currentZoom = zoomRef.current;
      const currentTransform = `scale(${currentZoom / 100})`;
      
      if (isCollapsed) {
        // Disable transition when collapsing to prevent position shift
        wrapperRef.current.style.transition = 'none';
        // Maintain transform even when collapsed
        wrapperRef.current.style.transform = currentTransform;
      } else {
        // Immediately set transform when expanding (before any layout recalculation)
        wrapperRef.current.style.transition = 'none';
        wrapperRef.current.style.transform = currentTransform;
        
        // Force synchronous layout to ensure transform is applied
        void wrapperRef.current.offsetHeight;
        
        // Re-enable transition after a brief delay
        requestAnimationFrame(() => {
          if (wrapperRef.current && !isCollapsed) {
            wrapperRef.current.style.transition = '';
            // Ensure transform is still correct using the ref value
            wrapperRef.current.style.transform = `scale(${zoomRef.current / 100})`;
          }
        });
      }
    }
  }, [isCollapsed]);
  
  // Update transform when zoom changes (but not during collapse/expand)
  useEffect(() => {
    if (wrapperRef.current && !isCollapsed) {
      wrapperRef.current.style.transform = `scale(${zoom / 100})`;
    }
  }, [zoom, isCollapsed]);

  const handleDownload = () => {
    if (documentUrl) {
      window.open(documentUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const renderDocument = () => {
    if (!documentUrl) {
      return (
        <div className="document-viewer-placeholder">
          <p>No document loaded</p>
          <p className="text-muted">Upload or select a document to view</p>
        </div>
      );
    }

    switch (documentType) {
      case "pdf":
        return (
          <div className="pdf-viewer-container" ref={containerRef}>
            <div 
              ref={wrapperRef}
              className="document-zoom-wrapper"
              style={{ 
                transform: `scale(${zoom / 100})`, 
                transformOrigin: "center center",
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "relative"
              }}
            >
              <iframe
                ref={iframeRef}
                src={`${documentUrl}#toolbar=0&navpanes=0&page=${currentPage}`}
                className="pdf-iframe"
                title="PDF Viewer"
              />
            </div>
          </div>
        );

      case "image":
        return (
          <div className="image-viewer-container" ref={containerRef}>
            <div
              ref={wrapperRef}
              className="document-zoom-wrapper"
              style={{ 
                transform: `scale(${zoom / 100})`, 
                transformOrigin: "center center",
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "relative"
              }}
            >
              <img
                src={documentUrl}
                alt={documentName || "Document"}
                className="document-image"
              />
            </div>
          </div>
        );

      case "word":
      case "doc":
      case "docx":
        return (
          <div className="word-viewer-container" ref={containerRef}>
            <div
              ref={wrapperRef}
              className="document-zoom-wrapper"
              style={{ 
                transform: `scale(${zoom / 100})`, 
                transformOrigin: "center center",
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "relative"
              }}
            >
              <iframe
                src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(documentUrl)}`}
                className="word-iframe"
                title="Word Viewer"
              />
            </div>
          </div>
        );

      default:
        return (
          <div className="document-viewer-placeholder">
            <p>Unsupported document type</p>
          </div>
        );
    }
  };

  return (
    <div className={`document-viewer ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="document-viewer-header">
        <div className="document-viewer-title-wrapper">
          {onToggleCollapse && (
            <button
              className="btn-text document-collapse-toggle"
              onClick={onToggleCollapse}
              title={isCollapsed ? 'Expand Document Viewer' : 'Collapse Document Viewer'}
            >
              {isCollapsed ? 'Expand' : 'Collapse'}
            </button>
          )}
          {!isCollapsed && <h5 className="document-viewer-title">Document Viewer</h5>}
        </div>
        {!isCollapsed && (
          <div className="document-viewer-actions">
            <button
              className="btn-icon"
              onClick={handleZoomOut}
              disabled={zoom <= 50}
              title="Zoom Out"
            >
              <ZoomOut size={18} />
            </button>
            <span className="zoom-level">{zoom}%</span>
            <button
              className="btn-icon"
              onClick={handleZoomIn}
              disabled={zoom >= 200}
              title="Zoom In"
            >
              <ZoomIn size={18} />
            </button>
            <button className="btn-icon" onClick={handleDownload} title="Download">
              <Download size={18} />
            </button>
            {onRegenerate && (
              <button className="btn-icon" onClick={onRegenerate} title="Regenerate">
                <RefreshCw size={18} />
              </button>
            )}
          </div>
        )}
      </div>

      {!isCollapsed && (
        <>
          <div className="document-viewer-content">
            {loading ? (
              <div className="document-loading">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              renderDocument()
            )}
          </div>

         
        </>
      )}
    </div>
  );
};

export default DocumentViewer;

